import type { Profile } from '@shady-pines/db';
import type { Password } from '@shady-pines/types';
import { isNonNil, ZodEmailAddress } from '@shady-pines/types';
import type { Session, SupabaseClient } from '@supabase/supabase-js';

export const handleSignIn = async <T = void>(
  supabase: SupabaseClient<any>,
  {
    emailOrUserName,
    password,
  }: {
    password: Password;
    emailOrUserName: string;
  },
  {
    onError,
    onSuccess,
    onPasswordResetRequired,
  }: {
    onError: (error: any) => T;
    onSuccess: (session: Session) => T;
    onPasswordResetRequired: () => T;
  },
  appVersion?: Profile['app_version'],
) => {
  const { data, error } = await (async () => {
    if (ZodEmailAddress.safeParse(emailOrUserName).success) {
      return supabase.functions.invoke<
        | {
            error: string;
          }
        | { result: 'signed-in'; session: Session }
        | { result: 'password-reset-required' }
      >('account/sign-in/basic/email', {
        method: 'POST',
        body: JSON.stringify({
          password,
          email: emailOrUserName,
          appVersion,
        }),
      });
    }
    return supabase.functions.invoke<
      | {
          error: string;
        }
      | { result: 'signed-in'; session: Session }
      | { result: 'password-reset-required' }
    >('account/sign-in/basic/user_name', {
      method: 'POST',
      body: JSON.stringify({
        password,
        user_name: emailOrUserName,
        appVersion,
      }),
    });
  })();

  if (isNonNil(error) || data === null || 'error' in data) {
    console.error(data);
    return onError(error ?? (data as any)?.error);
  }

  if (data.result === 'signed-in') {
    supabase.auth.setSession(data.session);
    return onSuccess(data.session);
  } else {
    return onPasswordResetRequired();
  }
};
